import { legalIdentityLines } from "@/config/legal";
import { A, ContactEmail, LegalDocument, LegalSection, LegalSubheading, List, P } from "./LegalLayout";

/**
 * Terms and Conditions for businesses using The Polished Version.
 * Billing, trial and plan facts mirror src/lib/plan-limits.ts,
 * src/lib/subscription-access.ts and the Stripe subscription functions.
 */

const TOC = [
  { id: "about", heading: "About these terms" },
  { id: "account", heading: "Your account" },
  { id: "free-trial", heading: "Free trial" },
  { id: "plans-billing", heading: "Plans, billing and renewal" },
  { id: "cancellation-refunds", heading: "Cancelling and refunds" },
  { id: "plan-limits", heading: "Plan limits and fair use" },
  { id: "website-domains", heading: "Your website and domains" },
  { id: "your-responsibilities", heading: "Your responsibilities" },
  { id: "customer-payments", heading: "Payments from your customers" },
  { id: "acceptable-use", heading: "Acceptable use" },
  { id: "intellectual-property", heading: "Intellectual property" },
  { id: "availability-support", heading: "Availability and support" },
  { id: "data-protection", heading: "Data protection and processing" },
  { id: "suspension-termination", heading: "Suspension and termination" },
  { id: "liability", heading: "Our liability" },
  { id: "indemnity", heading: "Indemnity" },
  { id: "changes", heading: "Changes to these terms" },
  { id: "general", heading: "General" },
  { id: "governing-law", heading: "Governing law" },
  { id: "contact", heading: "Contact us" },
];

const strong = (text: string) => <strong className="font-semibold text-foreground">{text}</strong>;

export function PlatformTerms() {
  const identity = legalIdentityLines();
  return (
    <LegalDocument
      slug="terms"
      title="Terms and Conditions"
      intro="These terms apply when a business signs up for and uses The Polished Version: the booking website, admin tools and related services we provide to cleaning businesses."
      toc={TOC}
      summary={
        <List
          items={[
            "The Polished Version is for businesses, not consumers.",
            "You get a 30-day free trial. If you choose a plan, we take your card through Stripe but don’t charge it until the trial ends.",
            "Plans renew automatically each month or year. You can cancel at any time and keep access until the end of the period you’ve paid for. We don’t refund part-used periods unless the law requires it.",
            "We take 0% commission on your bookings. Payments from your customers go through your own Stripe account or PayPal link, and their fees apply.",
            "You are responsible for your website content, your prices and terms, and your customers’ information. We process that information for you under the data processing terms below.",
          ]}
        />
      }
    >
      <LegalSection id="about" heading="About these terms">
        <P>
          These terms are a contract between The Polished Version (“we”, “us”, “our”) and the business that creates an account
          (“you”, “your”). By creating an account or using the service, you agree to them on behalf of your business. Our{" "}
          <A href="/privacy">Privacy Policy</A> and <A href="/cookies">Cookie Policy</A> explain how we handle personal information
          and browser storage.
        </P>
        {identity.length > 0 && <P>{identity.join(" ")}</P>}
        <P>In these terms:</P>
        <List
          items={[
            <>{strong("the service")} means The Polished Version platform, including your business website, booking pages, admin tools, emails we send for you, and any related support;</>,
            <>{strong("your website")} means the public website we host for your business;</>,
            <>{strong("your customers")} means people who book, enquire, message or otherwise deal with your business through the service; and</>,
            <>{strong("customer data")} means personal information about your customers and team members that you or they put into the service.</>,
          ]}
        />
      </LegalSection>

      <LegalSection id="account" heading="Your account">
        <List
          items={[
            "You must be at least 18 and use the service for business purposes. The service is not offered to consumers.",
            "The information you give us must be accurate and kept up to date.",
            "Keep your login details secure and don’t share them. Tell us straight away if you think someone has accessed your account without permission.",
            "You are responsible for everything done through your account, including by team members you invite. You decide what each team member can access by the role you give them.",
          ]}
        />
      </LegalSection>

      <LegalSection id="free-trial" heading="Free trial">
        <P>
          New businesses get a 30-day free trial. During the trial you can use the features of our Team plan. You don’t need to
          give card details to start a trial.
        </P>
        <P>
          You can choose a plan at any time during the trial. If you do, Stripe saves your card but we don’t charge it until the
          trial ends; choosing a plan early doesn’t restart or extend the 30 days. If the trial ends without a plan being chosen,
          your admin is locked except for Billing until you choose one. Your data is kept so you can carry on where you left off.
        </P>
      </LegalSection>

      <LegalSection id="plans-billing" heading="Plans, billing and renewal">
        <List
          items={[
            "We offer Solo, Team and Business plans, paid monthly or annually. The price you pay is the price shown on our pricing page at the time you subscribe.",
            "Payment is taken by card through Stripe, our payment provider. Your card details are held by Stripe, not by us.",
            "Your subscription renews automatically at the end of each monthly or annual period, and Stripe charges your card on each renewal, until you cancel.",
            "If a payment fails, Stripe will retry it and you’ll keep access with a warning in your admin. If payment still can’t be collected, your subscription ends and your access may be restricted until you subscribe again.",
            "You can change plan or payment card from Billing in your admin. Changes take effect as shown in the billing portal when you make them.",
            "We may change our prices. We will give you at least 30 days’ notice by email, and any new price will apply from your next renewal after the notice period. If you don’t want to continue at the new price, you can cancel before it applies.",
          ]}
        />
      </LegalSection>

      <LegalSection id="cancellation-refunds" heading="Cancelling and refunds">
        <P>
          You can cancel at any time from Billing in your admin, which opens Stripe’s secure billing portal. Cancelling stops
          future renewals. You keep full access until the end of the period you’ve already paid for, and you won’t be charged
          again. If you cancel during the free trial, you won’t be charged at all.
        </P>
        <P>
          Payments are not refundable, and we don’t give refunds or credits for part-used periods or unused features, unless the
          law requires us to.
        </P>
        <P>
          Cancelling your subscription doesn’t delete your data. To delete your business and its data, use{" "}
          {strong("Settings → Your data")} (see <A href="#suspension-termination">Suspension and termination</A>).
        </P>
      </LegalSection>

      <LegalSection id="plan-limits" heading="Plan limits and fair use">
        <List
          items={[
            <>{strong("Solo")}: one team member, no separate staff logins, no custom domain.</>,
            <>{strong("Team")}: up to five team members, staff logins and a custom domain.</>,
            <>{strong("Business")}: unlimited team members, staff logins and a custom domain.</>,
          ]}
        />
        <P>
          The current features of each plan are shown on our pricing page. If you move to a plan with lower limits, nothing is
          deleted: existing team members keep working, but you can’t add more until you are within the new plan’s limits.
        </P>
        <P>
          “Unlimited” means reasonable use by a single cleaning business. If your use is far beyond normal levels or affects the
          service for others (for example, very high volumes of email), we will contact you to find a solution before taking
          any action.
        </P>
      </LegalSection>

      <LegalSection id="website-domains" heading="Your website and domains">
        <P>
          Every business gets a website at its own address on thepolishedversion.com. Businesses on the Team or Business plan can
          connect a domain they own. You are responsible for registering and renewing your domain, keeping its DNS settings
          correct, and complying with your domain registrar’s terms.
        </P>
      </LegalSection>

      <LegalSection id="your-responsibilities" heading="Your responsibilities">
        <P>You are responsible for your business and how you use the service, including:</P>
        <List
          items={[
            "the content you publish, such as text, prices, photos, logos and reviews. It must be accurate, lawful and yours to use (or used with permission);",
            "your own terms with your customers, including your prices, cancellation and payment policies, and the quality and safety of the cleaning services you provide;",
            "meeting the laws that apply to your business, including consumer protection law (such as giving customers clear prices and any cancellation rights they are entitled to), and holding any insurance you need;",
            "your customers’ and team members’ personal information. You are the controller of that information: you must have a lawful basis to use it, tell people how you use it, respond to their requests, and pay any data protection fee you owe to the ICO. Your website includes a standard customer privacy notice; you should check it fits how you work and give customers any extra information they need;",
            "the accuracy of the bookings, quotes and invoices you create, and any tax you need to charge or account for; and",
            "following the terms of any third-party service you connect, such as Stripe, PayPal or Google (including Google’s rules on displaying reviews).",
          ]}
        />
      </LegalSection>

      <LegalSection id="customer-payments" heading="Payments from your customers">
        <List
          items={[
            "If you take card payments, you connect your own Stripe account. Payments are made directly to your Stripe account, and Stripe’s own terms (including its Connected Account Agreement) apply between you and Stripe.",
            "If you take PayPal payments, we simply show your customers the PayPal.me link you provide. PayPal’s terms apply.",
            "We take 0% commission on your bookings. Stripe and PayPal charge their own fees, which are taken from your payments.",
            "We are not a party to any contract or payment between you and your customers, and we don’t hold or receive your customers’ money. You are responsible for refunds, disputes and chargebacks with your customers and your payment provider.",
          ]}
        />
      </LegalSection>

      <LegalSection id="acceptable-use" heading="Acceptable use">
        <P>You must not use the service to:</P>
        <List
          items={[
            "break the law, or help anyone else to;",
            "publish anything false, misleading, defamatory, discriminatory, offensive or that infringes someone else’s rights, including fake reviews;",
            "send spam or unsolicited marketing, or contact people who haven’t agreed to hear from you where the law requires their agreement;",
            "upload viruses or harmful code, or try to access other businesses’ data, our systems or accounts you aren’t authorised to use;",
            "overload, disrupt, copy, scrape or reverse-engineer the service, except where the law allows; or",
            "resell the service or run websites for businesses other than your own without our written agreement.",
          ]}
        />
      </LegalSection>

      <LegalSection id="intellectual-property" heading="Intellectual property">
        <P>
          We (and our licensors) own the service, including its software, design, templates and our brand. While your account is
          active, we give you a non-exclusive, non-transferable licence to use the service for your own business under these
          terms.
        </P>
        <P>
          You keep ownership of your content and customer data. You give us permission to host, copy, display and process them as
          needed to provide the service, for example to show your services and photos on your website. If you send us
          suggestions or feedback, we may use them freely.
        </P>
      </LegalSection>

      <LegalSection id="availability-support" heading="Availability and support">
        <P>
          We work hard to keep the service running and secure, but we can’t promise it will always be available or free of
          errors. We may need to pause it for maintenance, and it relies on third-party providers such as our hosting, email and
          payment providers. We may improve, change or remove features over time; if we remove a significant feature you pay
          for, we will tell you in advance.
        </P>
        <P>
          Support is available by email at <ContactEmail />. We aim to reply within a reasonable time but don’t offer guaranteed
          response times.
        </P>
      </LegalSection>

      <LegalSection id="data-protection" heading="Data protection and processing">
        <P>
          Each party will comply with UK data protection law, including the UK GDPR and the Data Protection Act 2018. Our{" "}
          <A href="/privacy">Privacy Policy</A> covers information for which we are the controller, such as your account details.
        </P>
        <P>
          For customer data, you are the controller and we are your processor. This section is our data processing agreement
          under Article 28 of the UK GDPR.
        </P>

        <LegalSubheading>Details of the processing</LegalSubheading>
        <List
          items={[
            <>{strong("Subject matter and purpose")}: providing the service to you — hosting your website and booking pages, storing and organising your records, and sending emails and notifications on your behalf.</>,
            <>{strong("Duration")}: while your account exists, plus the 30-day deletion period after you delete your business.</>,
            <>{strong("Types of personal data")}: names, email addresses, phone numbers and addresses; booking, job and quote details; enquiries and callback requests; messages; reviews; invoices and payment status (not card numbers); job photos and checklists; and team members’ details, availability and assignments.</>,
            <>{strong("Data subjects")}: your customers and enquirers, and your team members.</>,
          ]}
        />

        <LegalSubheading>Our commitments as your processor</LegalSubheading>
        <List
          items={[
            "We process customer data only on your documented instructions, which are these terms and the way you set up and use the service, unless the law requires otherwise (in which case we will tell you first unless the law forbids it). We will tell you if we think an instruction breaks data protection law.",
            "Everyone we authorise to process customer data is bound by confidentiality.",
            "We apply appropriate technical and organisational security measures, as described in our Privacy Policy.",
            "You give us general authorisation to use the sub-processors listed in our Privacy Policy. We will put data protection terms in place with each one that are at least as protective as these, and remain responsible for them. We will update the list and tell you by email or in the admin before adding or replacing a sub-processor, so you can object on reasonable data protection grounds. If we can’t resolve your objection, you may end your subscription.",
            "Where customer data is transferred outside the UK, we make sure an approved safeguard is in place, such as the UK International Data Transfer Addendum or Agreement.",
            "We help you respond to people exercising their rights, mainly through self-service tools: you can export your data as CSV files and erase an individual customer’s details from your admin. We will pass on any request we receive directly.",
            "We give you reasonable help with security, breach notifications, data protection impact assessments and consultations with the ICO, taking into account the information available to us.",
            "We will tell you without undue delay after becoming aware of a personal data breach affecting customer data, with the information you need to meet your own obligations.",
            "When you delete your business, we delete customer data within 30 days, unless the law requires us to keep it. You can export it before deleting.",
            "We will make available the information reasonably needed to show we meet these obligations, and allow and contribute to audits by you or an auditor you appoint, on reasonable notice, no more than once a year (unless required by a regulator or after a breach), and subject to confidentiality.",
          ]}
        />

        <LegalSubheading>Your commitments as controller</LegalSubheading>
        <P>
          You confirm that you have a lawful basis for the customer data you put into the service and for the instructions you
          give us, and that you have given people the information the law requires.
        </P>
      </LegalSection>

      <LegalSection id="suspension-termination" heading="Suspension and termination">
        <P>
          You can stop using the service at any time by cancelling your subscription. To delete your business, the owner can use{" "}
          {strong("Settings → Your data")}: your website goes offline straight away, any subscription is cancelled, and your data
          is permanently deleted 30 days later. Please export anything you need first.
        </P>
        <P>
          We may suspend or close your account if you seriously or repeatedly break these terms, don’t pay, use the service
          unlawfully, or put the security of the service or other people at risk. Where it’s reasonable, we will warn you first
          and give you a chance to put things right. We may also end the service for everyone with at least 60 days’ notice, and
          give you time to export your data.
        </P>
        <P>
          The sections on payments already due, intellectual property, data protection, liability, indemnity and governing law
          continue to apply after your account ends.
        </P>
      </LegalSection>

      <LegalSection id="liability" heading="Our liability">
        <P>
          Nothing in these terms limits or excludes liability for death or personal injury caused by negligence, for fraud or
          fraudulent misrepresentation, or for anything else that cannot be limited or excluded by law.
        </P>
        <P>Subject to that:</P>
        <List
          items={[
            "we are not liable for loss of profits, revenue, business, bookings, goodwill or anticipated savings, or for any indirect or consequential loss;",
            "we are not liable for the acts or omissions of third-party services you choose to use, such as Stripe, PayPal, Google or your domain registrar, or for your dealings with your customers;",
            "we are not liable for loss of data you could have avoided by exporting it, or for problems caused by content or instructions you provide; and",
            "our total liability to you for all claims arising out of or in connection with these terms in any 12-month period is limited to the greater of the fees you paid us in the 12 months before the claim arose and £100.",
          ]}
        />
        <P>
          Except as set out in these terms, the service is provided “as is”, and all other warranties, conditions and terms
          implied by law are excluded to the extent the law allows.
        </P>
      </LegalSection>

      <LegalSection id="indemnity" heading="Indemnity">
        <P>
          You agree to compensate us for reasonable losses, costs and expenses (including reasonable legal fees) arising from any
          third-party claim caused by your content, the services you provide to your customers, your breach of these terms, or
          your breach of the law (including data protection law as a controller).
        </P>
      </LegalSection>

      <LegalSection id="changes" heading="Changes to these terms">
        <P>
          We may update these terms from time to time. If a change is significant, we will tell you by email or in the admin at
          least 30 days before it takes effect, unless it is needed sooner for legal or security reasons. If you keep using the
          service after the change takes effect, the new terms apply. If you don’t agree, you can cancel before then.
        </P>
      </LegalSection>

      <LegalSection id="general" heading="General">
        <List
          items={[
            "These terms, together with the policies they refer to, are the whole agreement between you and us about the service.",
            "Neither of us is responsible for delays or failures caused by events beyond our reasonable control.",
            "You may not transfer your rights under these terms without our written agreement. We may transfer ours to a business that takes over the service, as long as your rights are not reduced.",
            "If any part of these terms is found to be unenforceable, the rest stays in effect.",
            "If we don’t enforce a right straight away, we can still enforce it later.",
            "No one other than you and us has any right to enforce these terms under the Contracts (Rights of Third Parties) Act 1999.",
            "We will send notices to the email address on your account. You can send notices to us by email.",
          ]}
        />
      </LegalSection>

      <LegalSection id="governing-law" heading="Governing law">
        <P>
          These terms, and any dispute or claim arising out of or in connection with them, are governed by the law of England and
          Wales. The courts of England and Wales have exclusive jurisdiction.
        </P>
      </LegalSection>

      <LegalSection id="contact" heading="Contact us">
        <P>
          Questions about these terms? Email <ContactEmail />.
        </P>
      </LegalSection>
    </LegalDocument>
  );
}
